/**
 * The results area in the middle of the Copilot page, for the new chat
 * panel: the same place and order as before — a call waiting for approval,
 * then a query waiting for approval, then the result picked in the sidebar or
 * the chat, and otherwise "Your results land here" — drawn with the UI kit.
 */
import React from 'react';
import { Box, Typography } from '@mui/material';
import { ResultView, SqlApproval } from '../../../chat/panel';
import type { ApprovalAction } from '../../../chat/core/approvals';
import type { SQLApproval, WorkspaceArtifact } from '../stores/useCopilotStore';
import type { VoiceCallApproval } from '../../../types/voiceCall';
import { WorkspaceEmptyState } from './WorkspacePanel';
import VoiceCallApprovalCard from './VoiceCallApprovalCard';

export interface CopilotResultsPaneProps {
  artifact: WorkspaceArtifact | null;
  threadId: string | null;
  pendingSQLApproval: SQLApproval | null;
  onSQLApproval: (action: ApprovalAction, editedSql?: string, reason?: string) => void;
  pendingVoiceCallApproval: VoiceCallApproval | null;
  onVoiceCallApprove: () => void;
  onVoiceCallEditNumber: (number: string) => void;
  onVoiceCallReject: (reason: string) => void;
  onAddToDashboard: (artifact: WorkspaceArtifact) => void;
  onHydrate: (artifactId: string) => void;
  isStreaming: boolean;
  streamingAction?: string;
}

/** A decision, centred on the canvas rather than stretched across it. */
function Decision({ maxWidth, children }: { maxWidth: number; children: React.ReactNode }) {
  return (
    <Box
      sx={(t) => ({
        height: '100%',
        overflow: 'auto',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 3,
        bgcolor: t.tokens.surface.canvas,
      })}
    >
      <Box sx={{ width: '100%', maxWidth, maxHeight: '100%' }}>{children}</Box>
    </Box>
  );
}

export default function CopilotResultsPane({
  artifact,
  threadId,
  pendingSQLApproval,
  onSQLApproval,
  pendingVoiceCallApproval,
  onVoiceCallApprove,
  onVoiceCallEditNumber,
  onVoiceCallReject,
  onAddToDashboard,
  onHydrate,
  isStreaming,
  streamingAction,
}: CopilotResultsPaneProps) {
  // A pending call outranks everything: nothing moves until it's answered.
  if (pendingVoiceCallApproval) {
    return (
      <Decision maxWidth={520}>
        <VoiceCallApprovalCard
          approval={pendingVoiceCallApproval}
          onApprove={onVoiceCallApprove}
          onEditNumber={onVoiceCallEditNumber}
          onReject={onVoiceCallReject}
        />
      </Decision>
    );
  }
  if (pendingSQLApproval) {
    return (
      <Decision maxWidth={880}>
        <SqlApproval key={pendingSQLApproval.timelineId} approval={pendingSQLApproval} onDecide={onSQLApproval} variant="pane" />
      </Decision>
    );
  }
  if (!artifact) return <WorkspaceEmptyState isStreaming={isStreaming} streamingAction={streamingAction} />;

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', minWidth: 0 }}>
      <Box
        sx={(t) => ({
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          px: 2.5,
          py: 1.25,
          flexShrink: 0,
          borderBottom: `1px solid ${t.tokens.border.subtle}`,
          bgcolor: t.tokens.surface.raised,
        })}
      >
        <Typography variant="subtitle1" component="h2" noWrap title={artifact.title} sx={{ flex: 1, minWidth: 0 }}>
          {artifact.title || 'Result'}
        </Typography>
      </Box>
      <Box sx={{ flex: 1, minHeight: 0 }}>
        <ResultView
          key={artifact.id}
          artifact={artifact}
          variant="pane"
          threadId={threadId}
          onAddToDashboard={onAddToDashboard}
          onHydrate={onHydrate}
        />
      </Box>
    </Box>
  );
}
